import {ChangeDetectionStrategy, Component, computed, inject, output, signal} from '@angular/core';
import * as _ from 'lodash';
import {PANEL_DATA} from '../../../../commons/angular/services/panel.service';
import {TimekeepingFilterOption} from '../../models/timekeeping-list.model';

export interface CustomSelectFilterPanelData {
  options: TimekeepingFilterOption[];
  selectedIds: string[];
}

/** Row height of the virtual list, in px. Must match `.select-filter-item` in the stylesheet. */
const ITEM_SIZE_PX = 30;

/**
 * Multi-select list of one inline toolbar filter. The options are all in memory, so the search runs in the browser
 * and matches any part of the label - the API search only matches from the start of a word.
 */
require('./custom-select-filter-panel.scss');
@Component({
  standalone: false,
  selector: 'app-custom-select-filter-panel',
  templateUrl: './custom-select-filter-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomSelectFilterPanelComponent {

  // PanelService closes the panel on this output, so the name is part of its contract.
  // null means Cancel: the selection stays as it was.
  readonly onClose = output<string[] | null>();

  private readonly data = inject<CustomSelectFilterPanelData>(PANEL_DATA);
  private readonly options = this.data?.options || [];

  readonly itemSize = ITEM_SIZE_PX;
  readonly queryString = signal('');
  readonly selectedIds = signal<string[]>([...(this.data?.selectedIds || [])]);

  readonly visibleOptions = computed<TimekeepingFilterOption[]>(() => {
    const query = _.toLower(_.trim(this.queryString()));
    return query ? _.filter(this.options, (option) => _.includes(_.toLower(option.label), query)) : this.options;
  });
  readonly selectedKeys = computed<Record<string, boolean>>(() => _.zipObject(this.selectedIds(), _.map(this.selectedIds(), () => true)));
  readonly isEmpty = computed(() => !this.visibleOptions().length);
  readonly allVisibleSelected = computed(() => {
    const selectedKeys = this.selectedKeys();
    return !this.isEmpty() && _.every(this.visibleOptions(), (option) => selectedKeys[option.id]);
  });
  readonly someVisibleSelected = computed(() => {
    const selectedKeys = this.selectedKeys();
    return !this.allVisibleSelected() && _.some(this.visibleOptions(), (option) => selectedKeys[option.id]);
  });

  readonly trackById = (index: number, option: TimekeepingFilterOption) => option.id;

  onQueryChange(query: string) {
    this.queryString.set(query || '');
  }

  onToggle(id: string, isSelected: boolean) {
    this.selectedIds.update((ids) => isSelected ? _.union(ids, [id]) : _.without(ids, id));
  }

  /** Select All works on what the search shows, so a search followed by Select All picks just the matches. */
  onToggleAll() {
    const visibleIds = _.map(this.visibleOptions(), 'id');
    const isSelected = !this.allVisibleSelected();
    this.selectedIds.update((ids) => isSelected ? _.union(ids, visibleIds) : _.difference(ids, visibleIds));
  }

  onSubmit() {
    this.onClose.emit(this.selectedIds());
  }

  onReset() {
    this.onClose.emit([]);
  }

  onCancel() {
    this.onClose.emit(null);
  }
}
